import React, { forwardRef, HTMLAttributes } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/shadcn/components/ui/avatar';
import { cn } from '@/shadcn/lib/utils';
import { PROFILE_CONSTANTS } from './constants';

interface ProfileAvatarProps extends HTMLAttributes<HTMLDivElement> {
  /** URL of the avatar image */
  src?: string;
  /** Alt text for the avatar image */
  alt: string;
  /** Name to generate fallback initials from */
  name: string;
  /** Optional size override (defaults to h-11 w-11) */
  size?: string;
}

/**
 * ProfileAvatar
 * ---------------
 * A reusable avatar component for profile sections across sheet-based components.
 * Handles image display, fallback initials, and consistent sizing.
 *
 * Design tokens & classes:
 *  - Size: h-11 w-11 (44px x 44px) by default
 *  - Fallback: Generates initials from the provided name
 *  - Image: Uses shadcn Avatar component with proper alt text
 *
 * Usage:
 * ```tsx
 * <ProfileAvatar
 *   src={creator.profilePictureUrl}
 *   alt={creator.name}
 *   name={creator.name}
 * />
 * ```
 */
const ProfileAvatar = forwardRef<HTMLDivElement, ProfileAvatarProps>(({
  className,
  src,
  alt,
  name,
  size = PROFILE_CONSTANTS.DEFAULT_AVATAR_SIZE,
  ...props
}, ref) => {
  // Generate initials from name (first letter of each word, uppercase)
  const generateInitials = (name: string): string => {
    // Check if name is a non-empty string
    if (!name || typeof name !== 'string') {
      return '';
    }

    // Trim whitespace and split by spaces, filtering out empty words
    const words = name.trim().split(' ').filter((word) => word.length > 0);

    // Return empty string if no valid words found
    if (words.length === 0) {
      return '';
    }

    // Generate initials from first letter of each word
    return words
      .map((word) => word[0])
      .join('')
      .toUpperCase();
  };

  return (
    <div ref={ref} className={cn('flex-shrink-0', className)} {...props}>
      <Avatar className={cn(size)}>
        <AvatarImage src={src} alt={alt} />
        <AvatarFallback>
          {generateInitials(name)}
        </AvatarFallback>
      </Avatar>
    </div>
  );
});

ProfileAvatar.displayName = 'ProfileAvatar';

export default ProfileAvatar;
